import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
  CareType,
  OrganizationDedicatedGroupModelAllocationInput,
  OrganizationDgmAllocationCareTypeInput,
  OrganizationUtilizationWeek,
  useUpdateOrganizationDedicatedGroupModelAllocationsMutation,
} from '../../graphQL';
import { FinalButton } from '../../Components/FinalButton';
import { UtilizationRow, UtilizationTable } from '../../Components/UtilizationTable';
import { useCareTypeConfigContext } from '../../Components/CareTypeConfigContext';
import { thisWeekColumnCopy, pastColumnCopy, upcomingColumnCopy } from './UtilizationCopy';
import { useOrganizationId } from '../Organizations/util';
import { getCareTypeUtilization } from './utilizationCareTypes';
import {
  PairedFieldValidationErrors,
  updatePairedFieldValidationErrors,
} from '../../utils/pairedFieldValidation';

type UtilizationDashboardProps = {
  careType: CareType;
  editable?: boolean;
  utilizationRecords: OrganizationUtilizationWeek[];
  refetch: () => void;
  setCarePeriodAllocationsComplete: React.Dispatch<React.SetStateAction<boolean>>;
};

type EditedAllocationState = OrganizationDedicatedGroupModelAllocationInput & {
  careTypes: OrganizationDgmAllocationCareTypeInput[];
};

type AllocationValueKey = keyof Pick<
  OrganizationDgmAllocationCareTypeInput,
  'maxHours' | 'maxIntakeHours'
>;

const allocationValueKeyByInputType: Record<'hours' | 'intake', AllocationValueKey> = {
  hours: 'maxHours',
  intake: 'maxIntakeHours',
};

const pairedAllocationValueKeys: Record<AllocationValueKey, AllocationValueKey> = {
  maxHours: 'maxIntakeHours',
  maxIntakeHours: 'maxHours',
};

type UpdateMap = {
  [key: number]: EditedAllocationState;
};

type CareTypeAllocationValues = {
  [key in AllocationValueKey]?: number | null;
};

type ValidationErrors = PairedFieldValidationErrors;

type EditState = {
  allocations: UpdateMap;
  errors: ValidationErrors;
};

export const UtilizationDashboard = ({
  careType,
  editable,
  utilizationRecords,
  refetch,
  setCarePeriodAllocationsComplete,
}: UtilizationDashboardProps) => {
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const serviceTitle = getCareTypeDisplay(careType).serviceTitle;
  const [isEditable, setIsEditable] = useState(false);
  const [update] = useUpdateOrganizationDedicatedGroupModelAllocationsMutation();
  const [editState, setEditState] = useState<EditState>({ allocations: {}, errors: {} });
  const organizationId = useOrganizationId();
  const hasValidationErrors = Object.values(editState.errors).some(Boolean);

  const [completedRows, currentRows, upcomingRows] = useMemo(() => {
    return prepUtilizationRows(utilizationRecords, careType);
  }, [utilizationRecords, careType]);

  const getAllocationError = (isIntake: boolean, allocationId: number): string | undefined => {
    const valueKey = getAllocationValueKey(isIntake);
    return editState.errors[getValidationErrorKey(allocationId, careType, valueKey)];
  };

  const handleChange = (newValue: string, isIntake: boolean, allocationId: number) => {
    const valueKey = getAllocationValueKey(isIntake);

    setEditState(currentState => {
      const allocations = updateCareTypeAllocation(
        currentState.allocations,
        allocationId,
        careType,
        valueKey,
        getNumFromVal(newValue, isIntake)
      );

      return {
        allocations,
        errors: updateValidationErrors({
          currentErrors: currentState.errors,
          newValue,
          valueKey,
          allocation: allocations[allocationId],
          allocationId,
          careType,
          utilizationRecords,
        }),
      };
    });
  };

  // TODO: Handle errors from api
  const onSave = async () => {
    await update({
      variables: {
        organizationId,
        editedOrganizationDedicatedGroupModelAllocations: Object.values(editState.allocations),
      },
    });
    await refetch();
    setCarePeriodAllocationsComplete(false);
  };

  // TODO: handle attempts to cancel or change tabs with unsaved changed

  return (
    <div>
      {completedRows.length > 0 && (
        <div className="mb4">
          <UtilizationTable
            heading="Completed"
            rows={completedRows}
            columns={pastColumnCopy(serviceTitle)}
            editable={false}
          />
        </div>
      )}
      {currentRows.length > 0 && (
        <div className="mb4">
          <UtilizationTable
            heading="This Week"
            rows={currentRows}
            columns={thisWeekColumnCopy(serviceTitle)}
            editable={isEditable}
            // Columns for the current week are (Max Hours - EDITABLE, Hours Utilized, Hours Scheduled, Max Intakes - EDITABLE, Intakes Utilized, Intakes Scheduled)
            editableColumns={[[false], [true, false, false], [true, false, false]]}
            handleChange={handleChange}
            getError={getAllocationError}
          />
        </div>
      )}
      {upcomingRows.length > 0 && (
        <div className="mb4">
          <UtilizationTable
            heading="Upcoming"
            rows={upcomingRows}
            columns={upcomingColumnCopy(serviceTitle)}
            editable={isEditable}
            // Columns for the current week are (Max Hours - EDITABLE, Hours Scheduled, Max Intakes - EDITABLE, Intakes Scheduled)
            editableColumns={[[false], [true, false], [true, false]]}
            handleChange={handleChange}
            getError={getAllocationError}
          />
        </div>
      )}
      {editable && (
        <div>
          {!isEditable ? (
            <FinalButton
              kind="outline_black"
              onClick={() => {
                setIsEditable(true);
              }}
            >
              Edit {serviceTitle} Hours
            </FinalButton>
          ) : (
            <>
              <FinalButton
                kind="primary"
                className="mr2"
                disabled={hasValidationErrors}
                onClick={() => {
                  setIsEditable(false);
                  onSave();
                }}
              >
                Save Changes
              </FinalButton>
              <FinalButton
                kind="minimal_gray"
                onClick={() => {
                  setIsEditable(false);
                }}
              >
                Cancel
              </FinalButton>
            </>
          )}
        </div>
      )}
    </div>
  );
};

function validateAllocationValue(
  newValue: string,
  valueKey: AllocationValueKey,
  comparator: number | null | undefined
) {
  // Allow users to clear out values
  if (newValue === '') {
    return { isValid: true, errorString: '' };
  }

  const isIntake = isIntakeAllocationValueKey(valueKey);
  const newValNum = getNumFromVal(newValue, isIntake)!;

  if (isIntake) {
    // Validation for Intake Fields
    // First check that it's a whole number
    const intakeRegex = /^\d+$/;
    if (newValue.match(intakeRegex) === null) {
      return { isValid: false, errorString: 'Only positive whole numbers allowed.' };
    }
    // Then, if have a narrator, compare, otherwise valid
    if ((comparator === 0 || comparator) && newValNum > comparator) {
      return {
        isValid: false,
        errorString: 'Number of max intakes cannot exceed max bookable hours.',
      };
    }
  } else {
    // Validation for Hours Fields (Non Intake)
    // First check that it's a whole number or 0.25, .5, .75, .0
    const maxHoursRegex = /^\d+(\.(25|5|75|0)0*)?$/;
    if (newValue.match(maxHoursRegex) === null) {
      return {
        isValid: false,
        errorString: 'Only positive whole numbers and increments of .25, .5, and .75 allowed.',
      };
    }
    // Then, if have a comparator, compare, otherwise valid
    if ((comparator === 0 || comparator) && comparator > newValNum) {
      return {
        isValid: false,
        errorString: 'Number of max bookable hours cannot be less than max intakes',
      };
    }
  }
  return { isValid: true, errorString: '' };
}

function updateCareTypeAllocation(
  currentState: UpdateMap,
  allocationId: number,
  careType: CareType,
  valueKey: AllocationValueKey,
  value: number | null
): UpdateMap {
  const currentAllocation = currentState[allocationId] ?? { id: allocationId, careTypes: [] };
  const currentCareTypes = currentAllocation.careTypes;
  const careTypeIndex = currentCareTypes.findIndex(
    allocationCareType => allocationCareType.careType === careType
  );
  const updatedCareType = {
    ...(careTypeIndex >= 0 ? currentCareTypes[careTypeIndex] : { careType }),
    [valueKey]: value,
  };
  const updatedCareTypes =
    careTypeIndex >= 0
      ? currentCareTypes.map((allocationCareType, index) =>
          index === careTypeIndex ? updatedCareType : allocationCareType
        )
      : [...currentCareTypes, updatedCareType];

  return {
    ...currentState,
    [allocationId]: {
      id: allocationId,
      careTypes: updatedCareTypes,
    },
  };
}

function updateValidationErrors({
  currentErrors,
  newValue,
  valueKey,
  allocation,
  allocationId,
  careType,
  utilizationRecords,
}: {
  currentErrors: ValidationErrors;
  newValue: string;
  valueKey: AllocationValueKey;
  allocation: EditedAllocationState;
  allocationId: number;
  careType: CareType;
  utilizationRecords: OrganizationUtilizationWeek[];
}): ValidationErrors {
  const values = getCareTypeAllocationValues({
    allocation,
    allocationId,
    careType,
    utilizationRecords,
  });

  return updatePairedFieldValidationErrors({
    currentErrors,
    newValue,
    valueKey,
    values,
    getPairedValueKey: getPairedAllocationValueKey,
    getValidationErrorKey: currentValueKey =>
      getValidationErrorKey(allocationId, careType, currentValueKey),
    validateValue: validateAllocationValue,
  });
}

function getCareTypeAllocationValues({
  allocation,
  allocationId,
  careType,
  utilizationRecords,
}: {
  allocation: EditedAllocationState;
  allocationId: number;
  careType: CareType;
  utilizationRecords: OrganizationUtilizationWeek[];
}): CareTypeAllocationValues {
  const editedCareType = allocation.careTypes.find(
    allocationCareType => allocationCareType.careType === careType
  );
  const storedRecord = utilizationRecords.find(record => record.id === allocationId);
  const storedCareType = storedRecord ? getCareTypeUtilization(storedRecord, careType) : undefined;

  return {
    maxHours:
      editedCareType && 'maxHours' in editedCareType
        ? editedCareType.maxHours
        : storedCareType?.maxHours,
    maxIntakeHours:
      editedCareType && 'maxIntakeHours' in editedCareType
        ? editedCareType.maxIntakeHours
        : storedCareType?.maxIntakeHours,
  };
}

function getAllocationValueKey(isIntake: boolean): AllocationValueKey {
  return isIntake ? allocationValueKeyByInputType.intake : allocationValueKeyByInputType.hours;
}

function getPairedAllocationValueKey(valueKey: AllocationValueKey): AllocationValueKey {
  return pairedAllocationValueKeys[valueKey];
}

function isIntakeAllocationValueKey(valueKey: AllocationValueKey): boolean {
  return valueKey === allocationValueKeyByInputType.intake;
}

function getValidationErrorKey(
  allocationId: number,
  careType: CareType,
  valueKey: AllocationValueKey
) {
  return `${allocationId}-${careType}-${valueKey}`;
}

function prepUtilizationRows(
  utilizationRecords: OrganizationUtilizationWeek[],
  careType: CareType
): [UtilizationRow[], UtilizationRow[], UtilizationRow[]] {
  let weekCounter = 1;
  return utilizationRecords?.reduce(
    (result, a) => {
      const utilization = getCareTypeUtilization(a, careType);
      const rowWeekText = prepWeekText(a, weekCounter);

      if (moment(a.endDate) < moment().day('Sunday')) {
        result[0].push({
          id: a.id,
          rowData: [
            [rowWeekText],
            [utilization.maxHours, utilization.hoursUtilized],
            [utilization.maxIntakeHours, utilization.intakesUtilized],
          ],
        });
      } else if (moment(a.startDate) > moment().endOf('week')) {
        result[2].push({
          id: a.id,
          rowData: [
            [rowWeekText],
            [utilization.maxHours, utilization.hoursScheduled],
            [utilization.maxIntakeHours, utilization.intakesScheduled],
          ],
        });
      } else {
        result[1].push({
          id: a.id,
          rowData: [
            [rowWeekText],
            [utilization.maxHours, utilization.hoursUtilized, utilization.hoursScheduled],
            [utilization.maxIntakeHours, utilization.intakesUtilized, utilization.intakesScheduled],
          ],
        });
      }
      weekCounter += 1;
      return result;
    },
    [[], [], []] as [UtilizationRow[], UtilizationRow[], UtilizationRow[]]
  );
}

function prepWeekText(a: OrganizationUtilizationWeek, weekCounter: number) {
  const mmtStart = moment(a.startDate);
  const mmtEnd = moment(a.endDate);
  const diff = mmtEnd.diff(mmtStart, 'days') + 1;

  let rowWeekText = `Week ${weekCounter}: ${moment(a.startDate).format('M/D/YYYY')} - ${moment(
    a.endDate
  ).format('M/D/YYYY')}`;

  if (diff < 7) {
    rowWeekText += ` (${diff} day${diff > 1 ? 's' : ''})`;
  }
  return rowWeekText;
}

const getNumFromVal = (stringValue: string, isIntake: boolean): number | null => {
  if (stringValue === '') {
    return null;
  }

  return isIntake ? parseInt(stringValue, 10) : parseFloat(stringValue);
};
